
import { createClient } from '@supabase/supabase-js';
import { UserSettings } from '../types';

const SUPABASE_KEY_STORAGE = 'habitsync_supabase_key';
const SUPABASE_URL_STORAGE = 'habitsync_supabase_url';

export const getStoredSupabaseConfig = () => {
    return {
        url: localStorage.getItem(SUPABASE_URL_STORAGE) || import.meta.env.VITE_SUPABASE_URL || '',
        key: localStorage.getItem(SUPABASE_KEY_STORAGE) || import.meta.env.VITE_SUPABASE_ANON_KEY || ''
    };
};

export const saveSupabaseConfig = (url: string, key: string) => {
    localStorage.setItem(SUPABASE_URL_STORAGE, url);
    localStorage.setItem(SUPABASE_KEY_STORAGE, key);
};

let supabaseInstance: ReturnType<typeof createClient> | null = null;

export const getSupabase = () => {
    const { url, key } = getStoredSupabaseConfig();

    if (!url || !key) return null;

    if (!supabaseInstance) {
        supabaseInstance = createClient(url, key, {
            db: {
                schema: 'public' as any
            }
        } as any);
    } else {
        // Check if config changed (naive check, usually we just reload app)
        // For now, return singleton
    }
    return supabaseInstance;
};

// Helper for resetting client if config changes
export const resetSupabaseClient = () => {
    supabaseInstance = null;
};

export const getCheckInImageUrl = async (path: string): Promise<string> => {
    if (path.startsWith('data:')) return path;
    if (path.startsWith('http')) return path;

    const supabase = getSupabase();
    if (!supabase) return path;

    const { data } = supabase.storage
        .from('habit-images')
        .getPublicUrl(path);

    return data.publicUrl;
};

export const getCheckInImageUrls = async (paths: string[]): Promise<string[]> => {
    return Promise.all(paths.map(path => getCheckInImageUrl(path)));
};

export const getCurrentUser = async () => {
    const supabase = getSupabase();
    if (!supabase) return null;
    const { data: { user } } = await supabase.auth.getUser();
    return user;
};

export const uploadCheckInImage = async (file: File, userId: string) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error("Supabase not configured");

    const ext = file.name.split('.').pop();
    const fileName = `${userId}/${Date.now()}.${ext}`;
    const filePath = `${fileName}`;

    const { error } = await supabase.storage
        .from('habit-images')
        .upload(filePath, file);

    if (error) return { path: null, error };
    return { path: filePath, error: null };
};

export const uploadShopImage = async (file: File, userId: string) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error("Supabase not configured");

    const ext = file.name.split('.').pop();
    // [fix] uid must be the first path segment to satisfy Storage RLS policy
    const fileName = `${userId}/shop/${Date.now()}.${ext}`;
    const filePath = `${fileName}`;

    const { error } = await supabase.storage
        .from('habit-images')
        .upload(filePath, file);

    if (error) return { path: null, error };
    return { path: filePath, error: null };
};

export const uploadAvatarImage = async (file: File, userId: string) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error("Supabase not configured");

    const ext = file.name.split('.').pop();
    // [fix] uid must be the first path segment to satisfy Storage RLS policy
    const fileName = `${userId}/avatars/${Date.now()}.${ext}`;
    const filePath = `${fileName}`;

    const { error } = await supabase.storage
        .from('habit-images')
        .upload(filePath, file);

    if (error) return { path: null, error };
    return { path: filePath, error: null };
};

export const deleteCheckInImage = async (path: string) => {
    const supabase = getSupabase();
    if (!supabase) return;

    const { error } = await supabase.storage
        .from('habit-images')
        .remove([path]);

    return { error };
};

export const signIn = async (email: string, password: string) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error("Supabase not configured");

    const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
    });

    return { data, error };
};

export const signUp = async (email: string, password: string) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error("Supabase not configured");

    const { data, error } = await supabase.auth.signUp({
        email,
        password,
    });

    return { data, error };
};

export const initSupabase = (url: string, key: string) => {
    if (url && key) {
        saveSupabaseConfig(url, key);
    }
};

export const signOut = async () => {
    const supabase = getSupabase();
    if (!supabase) return;
    await supabase.auth.signOut();
};

export const onAuthStateChange = (callback: (user: any) => void) => {
    const supabase = getSupabase();
    if (!supabase) return () => {};

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        callback(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
};

export const backupToCloud = async (habits: any[], members: any[], items: any[], records: any[], healthProfiles: any[], healthRecords: any[], userId: string) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error("Supabase not configured");

    const backupData = {
        version: 6,
        exportedAt: new Date().toISOString(),
        habits,
        members,
        redemptionItems: items,
        redemptionRecords: records,
        healthProfiles,
        healthRecords
    };

    const blob = new Blob([JSON.stringify(backupData)], { type: 'application/json' });
    const fileName = `${userId}/${new Date().toISOString().replace(/[:.]/g, '-')}.json`;

    const { error } = await supabase.storage
        .from('habit-backups')
        .upload(fileName, blob, { upsert: true });

    return { error };
};

export const restoreFromCloud = async (fileName: string) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error("Supabase not configured");

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("User not logged in");

    const { data, error } = await supabase.storage
        .from('habit-backups')
        .download(`${user.id}/${fileName}`);

    if (error) return { data: null, error };

    const text = await data.text();
    return { data: JSON.parse(text), error: null };
};

export const getBackups = async (userId: string) => {
    const supabase = getSupabase();
    if (!supabase) return { data: [], error: null };

    const { data, error } = await supabase.storage
        .from('habit-backups')
        .list(userId + '/');

    if (error) return { data: [], error };
    
    if (!data || data.length === 0) return { data: [], error: null };

    const backupsWithData = await Promise.all(
        data.map(async (file) => {
            if (file.name === '.emptyFolderPlaceholder') return null;
            
            try {
                const { data: fileData } = await supabase.storage
                    .from('habit-backups')
                    .download(`${userId}/${file.name}`);
                
                if (fileData) {
                    const text = await fileData.text();
                    const parsedData = JSON.parse(text);
                    return {
                        ...file,
                        data: parsedData
                    };
                }
                return file;
            } catch (e) {
                console.error(`Failed to parse backup ${file.name}:`, e);
                return file;
            }
        })
    );

    return { data: backupsWithData.filter((b): b is NonNullable<typeof b> => b !== null), error: null };
};

export const deleteBackup = async (fileName: string) => {
    const supabase = getSupabase();
    if (!supabase) throw new Error("Supabase not configured");

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("User not logged in");

    const { error } = await supabase.storage
        .from('habit-backups')
        .remove([`${user.id}/${fileName}`]);

    return { error };
};
